import { useRef, useState, useEffect, useCallback } from "react";

// Tiny base64-encoded silent videos that trick the browser into thinking
// media is playing, preventing screen sleep on iOS Safari.
const SILENT_WEBM =
  "data:video/webm;base64,GkXfo0AgQoaBAUL3gQFC8oEEQvOBCEKCQAR3ZWJtQoeBAkKFgQIYU4BnQI0VSalmQCgq17FAAw9CQE2AQAZ3aGFtbXlXQUAGd2hhbW15RIlACECPQAAAAAAAFlSua0AxrkAu14EBY8WBAZyBACK1nEADdW5khkAFVl9WUDglhohAA1ZQOIOBAeBABrCBCLqBCB9DtnVAIueBAKNAHIEAAIAwAQCdASoIAAgAAUAmJaQAA3AA/vz0AAA=";

const SILENT_MP4 =
  "data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAAhtZGF0AAAA1m1vb3YAAABsbXZoZAAAAAAAAAAAAAAAAAAAA+gAAAAAAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAABidWR0YQAAAFptZXRhAAAAAAAAACFoZGxyAAAAAAAAAABtZGlyYXBwbAAAAAAAAAAAAAAAAC1pbHN0AAAAJal0b28AAAAdZGF0YQAAAAEAAAAATGF2ZjU4Ljc2LjEwMA==";

const KEEPALIVE_INTERVAL_MS = 10_000;

/**
 * Keeps the screen awake using multiple strategies in parallel:
 * 1. Wake Lock API — Chrome/Edge/Android, iOS Safari 16.4+
 * 2. Silent base64 video loop with manual seek — iOS Safari
 * 3. Web Audio API silent oscillator — creates an active audio session on iOS
 * 4. Fetch keepalive — periodic tiny network ping prevents iOS tab idle detection
 * 5. Periodic keepalive timer — re-acquires everything if the OS killed it
 *
 * IMPORTANT: `preventSleeping` must be called synchronously from a user gesture
 * (click/touch handler) so that video.play() and AudioContext are allowed by iOS.
 */
export interface AwakeMechanisms {
  wakeLock: boolean;
  video: boolean;
  audio: boolean;
  fetch: boolean;
}

export function useStayAwake() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fetchTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeRef = useRef(false);
  const [canSleep, setCanSleep] = useState(true);
  const [mechanisms, setMechanisms] = useState<AwakeMechanisms>({
    wakeLock: false,
    video: false,
    audio: false,
    fetch: false,
  });

  // Create the hidden video element for iOS with actual silent video content
  useEffect(() => {
    const video = document.createElement("video");
    // NOT muted — iOS treats muted videos as "safe to pause".
    // The video has no real audio so there's nothing audible.
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.setAttribute("loop", ""); // backup for browsers that honor it
    // Tiny but non-zero volume — iOS may optimise away volume=0
    video.volume = 0.01;
    Object.assign(video.style, {
      position: "absolute",
      top: "0",
      left: "0",
      width: "1px",
      height: "1px",
      opacity: "0.01",
    });

    const sourceWebm = document.createElement("source");
    sourceWebm.setAttribute("src", SILENT_WEBM);
    sourceWebm.setAttribute("type", "video/webm");
    video.appendChild(sourceWebm);

    const sourceMp4 = document.createElement("source");
    sourceMp4.setAttribute("src", SILENT_MP4);
    sourceMp4.setAttribute("type", "video/mp4");
    video.appendChild(sourceMp4);

    // iOS: manually seek back instead of relying on the loop attribute,
    // which iOS Safari can silently stop honoring.
    const onEnded = () => {
      if (!activeRef.current) return;
      video.currentTime = 0.01;
      video.play().catch(() => {});
    };
    const onTimeUpdate = () => {
      if (!activeRef.current) return;
      if (video.duration && video.currentTime > video.duration - 0.1) {
        video.currentTime = 0.01;
      }
    };
    video.addEventListener("ended", onEnded);
    video.addEventListener("timeupdate", onTimeUpdate);

    document.body.appendChild(video);
    videoRef.current = video;

    return () => {
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.pause();
      video.remove();
    };
  }, []);

  // Re-acquire everything when the tab becomes visible again
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !activeRef.current) return;

      // Re-acquire wake lock (fire-and-forget)
      acquireWakeLock();

      // iOS pauses video & suspends audio when tab is hidden — restart both
      if (videoRef.current?.paused) {
        videoRef.current.currentTime = 0.01;
        videoRef.current.play().catch(() => {});
      }
      if (audioCtxRef.current?.state === "suspended") {
        audioCtxRef.current.resume().catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  /** Fire-and-forget wake lock acquisition with auto-reacquire on release */
  const acquireWakeLock = useCallback((force = false) => {
    if (!("wakeLock" in navigator)) return;
    // Skip if we already hold an active lock (unless forced)
    if (wakeLockRef.current && !force) return;
    // Release old one first to avoid leaks
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
    navigator.wakeLock
      .request("screen")
      .then((lock) => {
        wakeLockRef.current = lock;
        setMechanisms((m) => ({ ...m, wakeLock: true }));
        // Auto-reacquire if the OS releases the lock (e.g. tab switch)
        lock.addEventListener("release", () => {
          wakeLockRef.current = null;
          setMechanisms((m) => ({ ...m, wakeLock: false }));
          if (activeRef.current && document.visibilityState === "visible") {
            acquireWakeLock(true);
          }
        });
      })
      .catch(() => {
        setMechanisms((m) => ({ ...m, wakeLock: false }));
      });
  }, []);

  /** Start a silent Web Audio oscillator — creates an active audio session on iOS */
  const startAudioContext = useCallback(() => {
    if (audioCtxRef.current) return;
    try {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioCtx) return;

      const ctx = new AudioCtx();

      // Create silence: oscillator → gain(0) → destination
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0; // completely silent
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();

      audioCtxRef.current = ctx;
    } catch {
      // AudioContext not available
    }
  }, []);

  const stopAudioContext = useCallback(() => {
    if (!audioCtxRef.current) return;
    audioCtxRef.current.close().catch(() => {});
    audioCtxRef.current = null;
  }, []);

  /** Start periodic fetch keepalive — prevents iOS from marking tab as idle */
  const startFetchKeepalive = useCallback(() => {
    if (fetchTimerRef.current) return;
    const doFetch = () => {
      if (!activeRef.current) return;
      // Tiny self-request with keepalive flag — browser keeps connection alive
      // even if the tab is about to be suspended
      fetch(window.location.href, {
        method: "HEAD",
        keepalive: true,
        cache: "no-store",
      }).catch(() => {});
    };
    doFetch();
    fetchTimerRef.current = setInterval(doFetch, 30_000); // every 30s is enough
    setMechanisms((m) => ({ ...m, fetch: true }));
  }, []);

  const stopFetchKeepalive = useCallback(() => {
    if (fetchTimerRef.current) {
      clearInterval(fetchTimerRef.current);
      fetchTimerRef.current = null;
    }
    setMechanisms((m) => ({ ...m, fetch: false }));
  }, []);

  const ensureVideoPlaying = useCallback(() => {
    const video = videoRef.current;
    if (!video || !activeRef.current) return;
    if (video.paused) {
      video.currentTime = 0.01;
      video.play().catch(() => {});
    }
  }, []);

  const ensureAudioRunning = useCallback(() => {
    if (!audioCtxRef.current || !activeRef.current) return;
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }
  }, []);

  /**
   * MUST be called synchronously from a user gesture (click/touch handler).
   * NOT async — awaiting anything before video.play() breaks the iOS user
   * gesture chain and causes the play() to be silently rejected.
   */
  const preventSleeping = useCallback(() => {
    activeRef.current = true;

    // Strategy 1: Wake Lock API (Chrome/Edge/Android, iOS Safari 16.4+)
    // Fire-and-forget — do NOT await, to preserve user gesture for video.play()
    acquireWakeLock();

    // Strategy 2: Silent video loop (iOS Safari) — MUST run in user gesture callstack
    if (videoRef.current) {
      videoRef.current.currentTime = 0.01;
      videoRef.current
        .play()
        .then(() => setMechanisms((m) => ({ ...m, video: true })))
        .catch(() => setMechanisms((m) => ({ ...m, video: false })));
    }

    // Strategy 3: Web Audio silent oscillator (iOS audio session)
    startAudioContext();
    setMechanisms((m) => ({ ...m, audio: !!audioCtxRef.current }));

    // Strategy 4: Fetch keepalive (prevents iOS idle tab detection)
    startFetchKeepalive();

    // Strategy 5: Periodic keepalive — re-acquires only what the OS killed
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      // Only re-acquire if the mechanism is actually dead
      if (!wakeLockRef.current) acquireWakeLock();
      ensureVideoPlaying();
      ensureAudioRunning();
      // Refresh mechanism status — reads current refs (no race since we
      // only call acquireWakeLock when ref is already null)
      setMechanisms({
        wakeLock: !!wakeLockRef.current,
        video: !!videoRef.current && !videoRef.current.paused,
        audio: audioCtxRef.current?.state === "running",
        fetch: !!fetchTimerRef.current,
      });
    }, KEEPALIVE_INTERVAL_MS);

    setCanSleep(false);
  }, [
    acquireWakeLock,
    startAudioContext,
    startFetchKeepalive,
    ensureVideoPlaying,
    ensureAudioRunning,
  ]);

  const allowSleeping = useCallback(() => {
    activeRef.current = false;

    // Stop the keepalive timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Release wake lock
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }

    // Stop video
    if (videoRef.current) {
      videoRef.current.pause();
    }

    // Stop audio context
    stopAudioContext();

    // Stop fetch keepalive
    stopFetchKeepalive();

    setCanSleep(true);
    setMechanisms({ wakeLock: false, video: false, audio: false, fetch: false });
  }, [stopAudioContext, stopFetchKeepalive]);

  /**
   * Call from a button click to re-attempt wake lock + video from a fresh user gesture.
   * Useful on iOS where the initial fire-and-forget may lose the gesture context.
   */
  const retryFromUserGesture = useCallback(() => {
    if (!activeRef.current) return;

    // Re-attempt wake lock from fresh user gesture (force even if held)
    acquireWakeLock(true);

    // Re-attempt video play from fresh user gesture
    if (videoRef.current) {
      videoRef.current.currentTime = 0.01;
      videoRef.current
        .play()
        .then(() => setMechanisms((m) => ({ ...m, video: true })))
        .catch(() => setMechanisms((m) => ({ ...m, video: false })));
    }

    // Resume audio if suspended
    if (audioCtxRef.current?.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }
  }, [acquireWakeLock]);

  return { canSleep, preventSleeping, allowSleeping, mechanisms, retryFromUserGesture };
}
